// Angular imports
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

// Rxjs imports
import { Subscription } from 'rxjs';

// CC-Common imports
import { IconConstants, Utils } from 'cc-common';

// Service imports
import { HelperService } from '../../../_services/shared-services/helper.service';
import { PrecisionService } from '../../../_services/shared-services/precision.service';
import { ContextManagerService } from '../../../_services/data-services/context-manager.service';
import { CiraHelpService } from '../../../_services/shared-services/cira-help.service';
import { ServerErrorService } from '../../../_services/shared-services/server-error.service';
import { WorkflowSearchService } from '../../_services/workflow-search.service';
import { DetailPopupsService } from '../../../cirabooks/shared-cirabooks-module/_services/detail-popups.service';

// Constant imports
import { CiraHelpKeyConstants } from '../../../_constants/cira-help-key-constants';
import { StringFormatConstants } from '../../../_constants/string-format-constants';

// Model imports
import { PropertyCommunityInfoResultDto } from '../../../_models/property-community-info-result-dto';
import { SubmittedInvoiceResultDto } from '../../_models/workflow-search/submitted-invoice-result-dto';
import { FileInfoDisplayResultDto } from '../../../_models/file-info-display-result-dto';

@Component({
    selector: 'app-workflow-submitted-invoices',
    templateUrl: './workflow-submitted-invoices.component.html',
    styleUrls: ['./workflow-submitted-invoices.component.scss']
})
export class WorkflowSubmittedInvoicesComponent implements OnInit {
    loadingVisible = false;
    showNotes = false;
    showLogs = false;
    showSubmitInvoice = false;
    noOfDays = 90;

    iconAdd = IconConstants.ADD;
    iconPDF: string = IconConstants.PDF_FILE_GRID;
    iconLogs: string = IconConstants.HISTORY;
    iconNotes: string = IconConstants.NOTES_AVAILABLE;
    iconEmptyNotes: string = IconConstants.NOTES_EMPTY;

    submittedInvoices: SubmittedInvoiceResultDto[];
    selectedRecord: SubmittedInvoiceResultDto;
    currencyFormat: any;
    dateFormat: any;
    helpLink: string;
    selectedContext: PropertyCommunityInfoResultDto;
    contextChangeSubscription: Subscription;
    isCorporateCirabook = false;

    private _timerId: any;

    constructor(
        private readonly route: ActivatedRoute,
        private readonly helperService: HelperService,
        private readonly precisionService: PrecisionService,
        private readonly contextService: ContextManagerService,
        private readonly ciraHelpService: CiraHelpService,
        private readonly errorService: ServerErrorService,
        private readonly workflowSearchService: WorkflowSearchService,
        private readonly detailsPopupService: DetailPopupsService
    ) {
        this.currencyFormat = this.precisionService.getCurrencyFormat();
        this.dateFormat = StringFormatConstants.dateFormat;
        this.route.queryParamMap.subscribe((params) => {
            if (params.get('uuid')) {
                const uuid = params.get('uuid');
                const noOfDays = parseInt(localStorage.getItem(uuid));
                if (!isNaN(noOfDays)) {
                    this.noOfDays = noOfDays;
                }
                localStorage.removeItem(uuid);
            }
        });
    }

    ngOnInit(): void {
        this.updateSelectedContext();
        this.contextChangeSubscription = this.contextService
            .getContext()
            .subscribe(() => this.updateSelectedContext.bind(this));
    }

    openPageInWindow(): void {
        const pageUrl = window.location.href.split('?')[0];
        const uuid = Utils.getUUID();
        localStorage.setItem(uuid, this.noOfDays.toString());
        window.open(`${pageUrl}?uuid=${uuid}`, '_blank').focus();
    }

    doRefresh(): void {
        clearTimeout(this._timerId);
        this._timerId = setTimeout(this.populateSubmittedInvoices.bind(this), 1000);
    }

    onSubmitInvoice(): void {
        this.showSubmitInvoice = true;
    }

    onWorkflowClick(rec: SubmittedInvoiceResultDto): void {
        this.detailsPopupService.updateSubjectWorkflowDetail(null, rec.communityId, rec.workflowId, rec.vendorId);
    }

    onVendorNameClick(data: SubmittedInvoiceResultDto): void {
        if (Utils.notNullAndDefined(data)) {
            const contextType = 'Community';
            const contextId = this.selectedContext.communityId;
            const vendorCode = data.vendorCode;
            const queryString = `contextType=${encodeURIComponent(
                contextType
            )}&contextId=${contextId}&vendorCode=${encodeURIComponent(vendorCode)}`;

            this.helperService.openDefaultVendorManagementPageInNewWindow(queryString);
        }
    }

    onDownloadFile(rec: FileInfoDisplayResultDto): void {
        this.helperService.downloadFileUsingUrl(rec.downloadUrl, rec.fileName);
    }

    onLogsClick(rec: SubmittedInvoiceResultDto): void {
        this.selectedRecord = rec;
        this.showLogs = true;
    }

    onLogsClose(e: any): void {
        this.selectedRecord = null;
        this.showLogs = false;
    }

    onNotesClick(rec: SubmittedInvoiceResultDto): void {
        this.selectedRecord = rec;
        this.showNotes = true;
    }

    onNotesClose(e: any): void {
        this.selectedRecord = null;
        this.showNotes = false;
    }

    onSubmitInvoiceClose(forceRefresh: boolean): void {
        this.showSubmitInvoice = false;
        if (!!forceRefresh) {
            this.populateSubmittedInvoices();
        }
    }

    private updateSelectedContext(): void {
        this.selectedContext = this.contextService.getSelectedContext();
        this.updateHelpLink();
        this.populateSubmittedInvoices();
    }

    private updateHelpLink(): void {
        if (!!this.selectedContext) {
            this.helpLink = this.ciraHelpService.getHelpLinkByKey(
                CiraHelpKeyConstants.cbAPWFSubmittedInvoices,
                this.selectedContext.communityId
            );
        } else {
            this.helpLink = this.ciraHelpService.getHelpLinkByKey(CiraHelpKeyConstants.cbAPWFSubmittedInvoices);
        }
    }

    private populateSubmittedInvoices(): void {
        this.loadingVisible = true;
        this.workflowSearchService.getMySubmittedInvoices(this.noOfDays).subscribe(
            (results: SubmittedInvoiceResultDto[]) => {
                if (Utils.notNullAndDefined(this.selectedContext)) {
                    this.submittedInvoices = results.filter((d) =>
                        Utils.equals(d.communityId, this.selectedContext.communityId)
                    );
                } else {
                    this.submittedInvoices = results;
                }
                this.loadingVisible = false;
            },
            (error) => {
                this.errorService.displayError(error);
                this.loadingVisible = false;
            }
        );
    }
}
